/**
 * DatePickerAccessibility
 *
 * Corrige dos problemas de accesibilidad de Element UI el-date-picker que
 * impedían cumplir WCAG 2.1 AA:
 *
 * 1) Atributos ARIA no propagados al <input>
 *    `aria-labelledby`, `aria-label` y `aria-describedby` puestos en
 *    `<el-date-picker>` se aplican al wrapper (`.el-date-editor`), pero NO
 *    al `<input>` real interno (`input.el-input__inner` o
 *    `input.el-range-input` en modo rango). Los lectores de pantalla
 *    anuncian el nombre/descripción del elemento con foco, y quien recibe
 *    el foco es el `<input>`.
 *    → Incumple 1.3.1 Info and Relationships y 4.1.2 Name, Role, Value.
 *
 * 2) La única instrucción de formato es el placeholder
 *    El `placeholder="dd-mm-aaaa"` desaparece al escribir o seleccionar una
 *    fecha, no lo anuncian todos los lectores de pantalla y suele tener
 *    poco contraste.
 *    → Incumple 3.3.2 Labels or Instructions (la instrucción debe ser
 *    persistente y estar asociada al campo).
 *
 * Soluciones (globales, sin tocar plantillas):
 * A. Se sobrescribe `ElDatePicker` y en `mounted` / `updated` se copian los
 *    atributos ARIA del wrapper al/los input(s) internos (y se limpian del
 *    wrapper para no duplicar el nombre accesible).
 * B. Si el date-picker tiene un `placeholder` con pinta de formato de fecha
 *    (p. ej. "dd-mm-aaaa", "HH:mm:ss", "MM/DD/YYYY"), se inserta a
 *    continuación del wrapper un `<p class="el-date-picker__format-hint">`
 *    con el texto "Formato: <placeholder>" y se enlaza al input mediante
 *    `aria-describedby`. Es visible siempre, aunque el campo tenga valor.
 *
 * Ambas soluciones son idempotentes y respetan cualquier `aria-describedby`
 * puesto manualmente por el desarrollador (en ese caso no se genera la
 * ayuda automática).
 *
 * Registro en main.js:
 *   import { registerAccessibleElDatePicker } from '@/components/directives/DatePickerAccessibility.js'
 *   // Después de Vue.use(ElementUI, { locale })
 *   registerAccessibleElDatePicker(Vue)
 *
 * Opcionalmente se puede personalizar el prefijo:
 *   registerAccessibleElDatePicker(Vue, { formatPrefix: 'Format: ' })
 */

// Atributos ARIA que Vue aplica al wrapper (.el-date-editor) pero deben
// estar en el <input> real para que los lectores de pantalla los usen.
const PROPAGATED_ARIA_ATTRS = [
  'aria-labelledby',
  'aria-label',
  'aria-describedby'
]

// Regex para decidir si un placeholder tiene aspecto de "formato de fecha"
// y por tanto merece que generemos la ayuda automática. Cubre patrones
// habituales: "dd-mm-aaaa", "dd/MM/yyyy", "HH:mm:ss", "MM/DD/YYYY", etc.
// Si el placeholder es texto libre ("Seleccionar fecha"), no se toca.
const DATE_FORMAT_PATTERN = /^[dDmMyYaAhHsS/\-.:\s]+$/

// Prefijo del texto de ayuda. Configurable en registerAccessibleElDatePicker.
let formatPrefix = 'Formato: '

// Contador para generar ids únicos en los <p> de ayuda.
let hintIdCounter = 0

// Bandera para inyectar la hoja de estilos una sola vez.
let stylesInjected = false

/**
 * Registra el override accesible de ElDatePicker.
 * Debe llamarse DESPUÉS de Vue.use(ElementUI).
 * @param {import('vue').VueConstructor} Vue
 * @param {{ formatPrefix?: string }} [options]
 */
export function registerAccessibleElDatePicker (Vue, options = {}) {
  if (typeof options.formatPrefix === 'string') {
    formatPrefix = options.formatPrefix
  }

  const OriginalElDatePicker = Vue.component('ElDatePicker')
  if (!OriginalElDatePicker) return

  Vue.component('ElDatePicker', {
    extends: OriginalElDatePicker,
    mounted () {
      syncAriaAttrsToInputs(this.$el)
      ensureFormatHint(this)
      setupFocusReturnOnPick(this)
    },
    updated () {
      syncAriaAttrsToInputs(this.$el)
      ensureFormatHint(this)
    },
    beforeDestroy () {
      detachPlaceholderObserver(this)
      const hint = this._a11yFormatHintEl
      if (hint && hint.parentNode) {
        hint.parentNode.removeChild(hint)
      }
      this._a11yFormatHintEl = null
      if (this.$el && this.$el.classList) {
        this.$el.classList.remove('el-date-editor--has-format-hint')
      }
    }
  })
}

/**
 * Copia los atributos ARIA relevantes del wrapper .el-date-editor a los
 * <input> internos. Actúa tanto en modo single (input.el-input__inner) como
 * en modo range (input.el-range-input).
 *
 * Idempotente: si el input ya tiene el atributo con el mismo valor, no hace
 * nada. Se puede llamar en mounted y updated sin coste apreciable.
 *
 * @param {HTMLElement} wrapper - El elemento root del el-date-picker (.el-date-editor)
 */
function syncAriaAttrsToInputs (wrapper) {
  if (!wrapper || typeof wrapper.querySelectorAll !== 'function') return

  // Recoger los atributos ARIA presentes en el wrapper
  const attrs = {}
  let hasAny = false
  PROPAGATED_ARIA_ATTRS.forEach(name => {
    const value = wrapper.getAttribute(name)
    if (value !== null) {
      attrs[name] = value
      hasAny = true
    }
  })
  if (!hasAny) return

  // Cubre modo single (.el-input__inner) y modo range (.el-range-input)
  const inputs = wrapper.querySelectorAll('input.el-input__inner, input.el-range-input')
  if (!inputs.length) return

  inputs.forEach(input => {
    Object.keys(attrs).forEach(name => {
      if (input.getAttribute(name) !== attrs[name]) {
        input.setAttribute(name, attrs[name])
      }
    })
  })

  // Evitamos duplicar el nombre/descripción accesibles: si el wrapper aún
  // tiene el atributo, se lo quitamos (deben estar en el <input>, que es
  // quien recibe foco).
  Object.keys(attrs).forEach(name => {
    wrapper.removeAttribute(name)
  })
}

/**
 * WCAG 2.1 AA - 2.4.3 Orden del foco.
 *
 * Cuando el usuario selecciona una fecha en el panel (con teclado o ratón),
 * Element UI cierra el popover pero NO devuelve el foco al input; el foco se
 * pierde al <body>. Aquí devolvemos el foco al input de referencia justo
 * después de una selección real.
 *
 * Consideraciones para no romper otros comportamientos existentes:
 *  - Solo actuamos tras una selección real (evento 'pick' del panel), no al
 *    cerrar con Escape, Tab o el botón de limpiar (esos flujos ya los gestionan
 *    los hacks del mixin: focusElementByKey / toggleDisplayPopoverDatepicker).
 *  - Al enfocar el input, Element UI reabre el picker (handleFocus). Lo
 *    suprimimos poniendo pickerVisible=false en el mismo tick, por lo que Vue
 *    agrupa ambos cambios y el popover permanece cerrado (sin parpadeo).
 *  - En modo datetime la selección de la fecha mantiene el panel abierto
 *    (pickerVisible sigue true); solo devolvemos el foco cuando el panel se
 *    cierra de verdad (al confirmar), respetando el flujo de selección de hora.
 *
 * @param {import('vue').default} vm - La instancia de ElDatePicker (Picker.vue)
 */
function setupFocusReturnOnPick (vm) {
  if (vm._a11yFocusReturnSetup) return
  vm._a11yFocusReturnSetup = true

  vm.$watch('pickerVisible', (visible, oldVisible) => {
    if (visible) {
      // El panel (vm.picker) se monta antes de poner pickerVisible=true, así que
      // aquí ya existe. Nos enganchamos a su evento 'pick' una sola vez.
      if (vm.picker && !vm.picker._a11yPickHooked) {
        vm.picker._a11yPickHooked = true
        vm.picker.$on('pick', () => {
          // Marcamos que el próximo cierre proviene de una selección real.
          vm._a11yJustPicked = true
        })
      }
    } else if (oldVisible && vm._a11yJustPicked) {
      vm._a11yJustPicked = false
      // Tras el cierre por selección, devolvemos el foco al input.
      // setTimeout(0) para ejecutar después del cierre síncrono de Element UI.
      setTimeout(() => {
        const input = getReferenceInput(vm)
        if (!input) return
        input.focus()
        // handleFocus (síncrono) reabrió el picker; lo cerramos en el mismo tick
        // para que Vue agrupe los cambios y el popover no quede abierto.
        if (vm.pickerVisible) vm.pickerVisible = false
      }, 0)
    }
  })
}

/**
 * Devuelve el <input> de referencia (el que recibe foco) del el-date-picker.
 * @param {import('vue').default} vm
 * @returns {HTMLInputElement|null}
 */
function getReferenceInput (vm) {
  const ref = vm.$refs && vm.$refs.reference
  if (ref && ref.$el) {
    return ref.$el.querySelector('input.el-input__inner') || ref.$el.querySelector('input')
  }
  if (vm.$el && typeof vm.$el.querySelector === 'function') {
    return vm.$el.querySelector('input.el-input__inner')
  }
  return null
}

/**
 * Si el date-picker tiene un placeholder con aspecto de formato de fecha,
 * inserta DENTRO del wrapper (.el-date-editor) un <p> visible posicionado
 * en absoluto justo debajo del input, y lo enlaza al input via
 * aria-describedby.
 *
 * El wrapper recibe la clase `el-date-editor--has-format-hint` que le da
 * `position:relative` (para anclar el hint) y un `margin-bottom` que
 * reserva espacio vertical. Al insertarlo como hijo con position:absolute
 * NO se rompe la línea horizontal, por lo que los hermanos que hubiese
 * junto al date-picker (por ejemplo `base-clear-input-button`) siguen
 * visualmente a su derecha.
 *
 * Además, para evitar que los lectores de pantalla anuncien dos veces la
 * misma información (primero el placeholder nativo y luego el texto del
 * aria-describedby), retiramos el `placeholder` del input cuando activamos
 * la ayuda. Como Element UI y Vue pueden re-aplicarlo en cada patch interno
 * (los `datetime` pickers repatchan mucho al cambiar el valor), NO nos
 * limitamos al hook `updated`: instalamos un MutationObserver sobre el
 * atributo `placeholder` del input que lo retira de forma inmediata cada
 * vez que reaparece, aprovechando la ocasión para actualizar la fuente de
 * la verdad por si el placeholder ha cambiado.
 *
 * Reglas:
 *  - Solo modo single (input.el-input__inner). En modo rango hay dos
 *    placeholders separados; se omite para no complicar la solución.
 *  - Si el desarrollador ya puso aria-describedby manualmente (que no
 *    apunte a nuestro <p>), se respeta y no se genera nada.
 *  - Si el placeholder no parece un formato de fecha (texto libre), no se
 *    genera nada; si antes había ayuda, se retira y se libera el input.
 *  - Idempotente: reutiliza el mismo <p> entre renders y sólo actualiza el
 *    texto si cambia el placeholder.
 *
 * @param {import('vue').default} vm - La instancia de ElDatePicker
 */
function ensureFormatHint (vm) {
  const wrapper = vm.$el
  if (!wrapper || typeof wrapper.querySelector !== 'function') return

  // Solo modo single
  const input = wrapper.querySelector('input.el-input__inner')
  if (!input) return

  // Vue vuelve a aplicar el placeholder en cada patch. Si en el render
  // anterior ya lo habíamos retirado, aquí veremos "" hasta el próximo
  // cambio en Vue. Guardamos el valor "fuente de la verdad" en la instancia
  // para poder derivar siempre el texto de la ayuda.
  const currentPlaceholder = input.getAttribute('placeholder') || ''
  if (currentPlaceholder) {
    vm._a11ySourcePlaceholder = currentPlaceholder
  }
  const sourcePlaceholder = vm._a11ySourcePlaceholder || ''

  if (!sourcePlaceholder || !DATE_FORMAT_PATTERN.test(sourcePlaceholder)) {
    // No procede ayuda automática: retiramos la que hubiese antes y no
    // tocamos el placeholder (lo dejamos como lo puso el desarrollador).
    removeExistingHint(vm, input)
    return
  }

  // Respetar aria-describedby manual (que no sea el nuestro).
  const currentDescribedBy = input.getAttribute('aria-describedby')
  const existingHint = vm._a11yFormatHintEl
  if (
    currentDescribedBy &&
    (!existingHint || currentDescribedBy !== existingHint.id)
  ) {
    return
  }

  ensureStyles()

  const expectedText = formatPrefix + sourcePlaceholder

  if (existingHint && existingHint.isConnected) {
    // Ya existe: sincronizar texto y aria-describedby por si cambió el placeholder
    if (existingHint.textContent !== expectedText) {
      existingHint.textContent = expectedText
    }
    if (input.getAttribute('aria-describedby') !== existingHint.id) {
      input.setAttribute('aria-describedby', existingHint.id)
    }
  } else {
    // Crear el <p> como HIJO del wrapper para poder posicionarlo en
    // absoluto sin romper la línea de los hermanos del date-picker
    // (por ejemplo el botón de limpiar).
    const hint = document.createElement('p')
    hint.className = 'el-date-picker__format-hint'
    hint.id = 'el-date-picker-fmt-hint-' + (++hintIdCounter)
    hint.textContent = expectedText

    wrapper.appendChild(hint)
    wrapper.classList.add('el-date-editor--has-format-hint')
    vm._a11yFormatHintEl = hint

    input.setAttribute('aria-describedby', hint.id)
  }

  // Evitar el doble anuncio del lector de pantalla: retiramos el placeholder
  // del input. Element UI puede re-aplicarlo por su cuenta en cada patch
  // interno (sobre todo en pickers de tipo datetime), por eso instalamos un
  // MutationObserver que lo vuelva a retirar en cuanto reaparezca.
  if (input.hasAttribute('placeholder')) {
    input.removeAttribute('placeholder')
  }
  attachPlaceholderObserver(vm, input)
}

/**
 * Instala un MutationObserver sobre el atributo `placeholder` del input
 * para retirarlo cada vez que Vue o Element UI lo re-apliquen. Aprovecha
 * el paso para mantener actualizada la fuente de la verdad y sincronizar
 * el texto del hint si el placeholder cambia.
 * @param {import('vue').default} vm
 * @param {HTMLInputElement} input
 */
function attachPlaceholderObserver (vm, input) {
  if (vm._a11yPlaceholderObserver) return
  if (typeof MutationObserver === 'undefined') return

  const observer = new MutationObserver(() => {
    // Si el hint ya no está activo (removeExistingHint / beforeDestroy),
    // este observer se habrá desconectado antes; comprobación defensiva.
    if (!vm._a11yFormatHintEl) return

    const current = input.getAttribute('placeholder')
    if (!current) return

    // Aprovechamos para actualizar la fuente de la verdad y el texto del hint
    // si el placeholder ha cambiado a otro formato válido.
    if (DATE_FORMAT_PATTERN.test(current)) {
      vm._a11ySourcePlaceholder = current
      const expectedText = formatPrefix + current
      if (vm._a11yFormatHintEl.textContent !== expectedText) {
        vm._a11yFormatHintEl.textContent = expectedText
      }
    }

    // Retirarlo de inmediato para evitar el doble anuncio. Esto dispara
    // otra mutación que en la siguiente iteración verá placeholder=null y
    // no hará nada, por lo que no hay bucle infinito.
    input.removeAttribute('placeholder')
  })

  observer.observe(input, { attributes: true, attributeFilter: ['placeholder'] })
  vm._a11yPlaceholderObserver = observer
  vm._a11yObservedInput = input
}

/**
 * Desconecta el MutationObserver del placeholder, si estaba activo.
 * @param {import('vue').default} vm
 */
function detachPlaceholderObserver (vm) {
  if (vm._a11yPlaceholderObserver) {
    vm._a11yPlaceholderObserver.disconnect()
    vm._a11yPlaceholderObserver = null
    vm._a11yObservedInput = null
  }
}

/**
 * Retira la ayuda automática previa (si existía), limpia el
 * aria-describedby que hubiéramos puesto y desactiva el observer del
 * placeholder para que el input vuelva a comportarse como Element UI
 * quiera.
 * @param {import('vue').default} vm
 * @param {HTMLElement} input
 */
function removeExistingHint (vm, input) {
  detachPlaceholderObserver(vm)
  const hint = vm._a11yFormatHintEl
  if (!hint) return
  const wrapper = hint.parentNode
  if (wrapper) {
    wrapper.removeChild(hint)
    if (wrapper.classList) {
      wrapper.classList.remove('el-date-editor--has-format-hint')
    }
  }
  if (input && input.getAttribute('aria-describedby') === hint.id) {
    input.removeAttribute('aria-describedby')
  }
  vm._a11yFormatHintEl = null
}

/**
 * Inyecta una sola vez la hoja de estilos para .el-date-picker__format-hint.
 * Colores pensados para funcionar tanto en tema claro como en el modo oscuro
 * de la aplicación (ancestro con clase .dark).
 */
function ensureStyles () {
  if (stylesInjected) return
  if (typeof document === 'undefined') return
  stylesInjected = true

  const style = document.createElement('style')
  style.setAttribute('data-el-date-picker-a11y', '')
  style.textContent =
    // Wrapper: reservamos hueco vertical y creamos contexto de posicionamiento
    // solo cuando hay hint activo, para no afectar a date-pickers sin placeholder
    // de formato.
    '.el-date-editor.el-date-editor--has-format-hint{' +
      'position:relative;' +
      'margin-bottom:18px;' +
    '}' +
    // El hint se posiciona en absoluto respecto al wrapper para no romper
    // el flujo horizontal (mantiene visualmente a la derecha cualquier
    // hermano como el botón de limpiar).
    '.el-date-picker__format-hint{' +
      'position:absolute;' +
      'top:100%;' +
      'left:0;' +
      'margin:2px 0 0 0;' +
      'padding:0;' +
      'font-size:12px;' +
      'line-height:1.2;' +
      'white-space:nowrap;' +
      'pointer-events:none;' +
      'color:#64748b;' + // contraste AA sobre fondo blanco
    '}' +
    '.dark .el-date-picker__format-hint{' +
      'color:#cbd5e1;' + // contraste AA sobre fondo oscuro
    '}'
  document.head.appendChild(style)
}
